/**
 * A standalone helper function to clean and format a string.
 */
const cleanText = (text: string, limit = 40): string => {
    if (!text) return '';

    return text.trim().replace(/\s+/g, ' ').substring(0, limit);
};

const getTargetIdentifier = (element: Element | null): string | null => {
    if (!element) {
        return null;
    }

    const tagName = element.tagName.toLowerCase();

    // 1. Prioritize the 'alt' attribute for images and other media
    const altText = element.getAttribute('alt');
    if (altText) {
        return `${tagName}[alt=${cleanText(altText)}]`;
    }

    // 2. Use the element's text content as the primary identifier
    const text = element.textContent;
    if (text && cleanText(text)) {
        return `${tagName}[text=${cleanText(text)}]`;
    }

    // 3. Fallback to just the tag name if there's no descriptive text
    return tagName;
};

export class IdentifierService {
    /**
     * Regex to match and block common utility classes from frameworks like Bootstrap.
     * This can be customized to fit your specific CSS framework.
     */
    private utilityClassBlocklist = /^(d-|flex-|justify-|align-|col-|m-|p-|table$|table-|w-|h-|border|shadow|rounded|g-|fs-|fw-)/;

    /**
     * Generates the complete hybrid identifier by combining the ancestor path
     * and the target identifier.
     * @param element The element to generate an identifier for.
     * @returns The final, complete identifier, or null if the element is invalid.
     */
    public generateIdentifier(element: Element | null): string | null {
        if (!element) {
            return null;
        }

        const targetId = getTargetIdentifier(element);
        const ancestorPath = this.getAncestorPath(element);

        return ancestorPath ? `${ancestorPath} > ${targetId}` : targetId;
    }

    /**
     * Creates a contextual path by walking up the DOM, ignoring common utility classes.
     * @param element The element that was clicked.
     * @returns The ancestor path string.
     */
    private getAncestorPath(element: Element): string {
        const pathParts: string[] = [];
        let current = element.parentElement;

        while (current && current.tagName !== 'BODY') {
            let part = '';

            if (current.id) {
                part += `#${current.id}`;
            }

            const descriptiveClasses = Array.from(current.classList).filter(
                (cls) => cls.length > 4 && !this.utilityClassBlocklist.test(cls),
            );

            if (descriptiveClasses.length > 0) {
                part += `.${descriptiveClasses.join('.')}`;
            }

            if (part) {
                pathParts.unshift(part);
            }

            current = current.parentElement;
        }

        return pathParts.join(' > ');
    }
}
